import React, { useEffect, useRef, useState } from "react";
import useDeferredLoad from "../../rendering/useDeferredLoad";
import "./introFilm.css";

// The first film on the page: an advisor speaking in an AVRAH studio, in two
// of the app's layouts, with the elements AVRAH lays on her video. Each
// element is the bitmap AVRAH's render service draws for it
// (public/home/intro-film/, rendered at 1920x1080 from the app's own
// templates in the Dinamico element style with the default gold brand),
// placed and timed as the app's engine composites it, so the page shows what
// the app makes.
const FILM = "/home/intro-film";
const FRAME = [1920, 1080];

// The app's layouts the scenes use, as [x, y, width, height] on the frame:
// where the camera goes, the part of its picture shown there, and the slot
// beside it. Relatore gives the camera the whole frame; Affiancato puts half
// its picture on the left half and fills the right half with the slot's
// element. The app shows the picture's centre half; the advisor stands left
// of centre in this clip, so the half shown is the one centred on her, as she
// would sit to record in this layout.
const LAYOUTS = {
  speaker: { camera: [0, 0, 1920, 1080], picture: [0, 0, 1920, 1080] },
  sideBySide: { camera: [0, 0, 960, 1080], picture: [320, 0, 960, 1080], slot: [960, 0, 960, 1080] },
};

// The scenes, each from the second of the clip it starts at to the next.
export const SCENES = [
  { from: 0, layout: "speaker" },
  { from: 6.6, layout: "sideBySide" },
];

// When each element shows, in seconds of the clip. The name bar runs as the
// app's temporary lower third: 300 ms in, held 5 s, 300 ms out. The agenda,
// the app's checklist filling Affiancato's slot, and the ticker carrying the
// disclaimer make the second scene, to the clip's end.
export const CUES = { name: [0.6, 5.9], list: [6.6, Infinity], ticker: [6.6, Infinity] };

// Where the name bar sits: the app's small lower third, 46 px from the
// bottom-left corner.
const NAME_BAR = [46, 802, 760, 232];

// "Messaggio a scopo informativo", in the app's Ticker element: the type its
// News Ticker preset draws in the default theme, the same in every element
// style, on one unbroken band along the frame's bottom edge in the preset's
// box colours. The type's strip leaves the rest of a half frame clear, so
// its copies, laid end to end, run two to the frame and crawl right to left
// at 90 px a second.
const TICKER = { width: 960, height: 83, top: 997, speed: 90 };
const BAND = [
  [0, "rgba(44,44,46,.91)"],
  [0.012, "rgba(44,44,46,.91)"],
  [0.012, "rgba(12,12,16,.9)"],
  [1, "rgba(10,10,11,.9)"],
];
// Copies enough to cover the frame wherever the crawl stands.
const COPIES = Math.ceil(FRAME[0] / TICKER.width) + 1;

// The engine's crossfade between scenes: the last frame of one, over the
// next, fading out in 8 frames.
const CROSSFADE = { duration: 267, easing: "linear" };

export const layoutAt = (time) => SCENES.filter((scene) => scene.from <= time).pop().layout;

// Where the copy of the ticker's strip that covers the frame's left edge
// starts, in frame pixels, at `time` seconds of the clip. As in the engine,
// the strip enters at the frame's right edge when the ticker first shows.
export function tickerStart(time) {
  const crawled = TICKER.speed * (time - CUES.ticker[0]);
  const behind = (((FRAME[0] - crawled) % TICKER.width) + TICKER.width) % TICKER.width;
  return behind === 0 ? 0 : behind - TICKER.width;
}

const percent = (part, whole) => `${(100 * part) / whole}%`;

// Draws the frame `film` shows, in `layout` at `time` seconds, onto `canvas`
// and fades it out over the next scene, as the engine does.
function fadeOut(canvas, film, { layout, time }) {
  const { width, height } = film.getBoundingClientRect();
  canvas.width = Math.round(width * window.devicePixelRatio);
  canvas.height = Math.round(height * window.devicePixelRatio);
  const paint = canvas.getContext("2d");
  paint.scale(canvas.width / FRAME[0], canvas.height / FRAME[1]);
  const video = film.querySelector("video");
  const { camera, picture } = LAYOUTS[layout];
  const toVideo = video.videoWidth / FRAME[0];
  paint.drawImage(video, ...picture.map((n) => n * toVideo), ...camera);
  film.querySelectorAll('img.if-layer[data-shown="true"]').forEach((img) => {
    paint.drawImage(img, ...img.dataset.rect.split(" ").map(Number));
  });
  if (film.querySelector(".if-ticker").dataset.shown === "true") {
    const band = paint.createLinearGradient(0, TICKER.top, 0, TICKER.top + TICKER.height);
    BAND.forEach(([stop, colour]) => band.addColorStop(stop, colour));
    paint.fillStyle = band;
    paint.fillRect(0, TICKER.top, FRAME[0], TICKER.height);
    const strip = film.querySelector(".if-crawl img");
    for (let k = 0; k < COPIES; k += 1) {
      paint.drawImage(strip, tickerStart(time) + k * TICKER.width, TICKER.top, TICKER.width, TICKER.height);
    }
  }
  canvas.animate([{ opacity: 1 }, { opacity: 0 }], { ...CROSSFADE, fill: "forwards" });
}

// The scene and the elements the clip is at, and the ticker's crawl: read on
// every frame while it plays, and whenever it is moved. Unchanged readings
// cost no render; the crawl moves the strip without one. A scene that
// changes while the clip plays crossfades into the next.
function useClip(filmRef, fadeRef) {
  const [layout, setLayout] = useState(SCENES[0].layout);
  const [showing, setShowing] = useState("");
  useEffect(() => {
    const film = filmRef.current;
    const video = film.querySelector("video");
    const crawl = film.querySelector(".if-crawl");
    let last = { layout: layoutAt(video.currentTime), time: video.currentTime };
    let frame = 0;
    const read = () => {
      const time = video.currentTime;
      const now = { layout: layoutAt(time), time };
      if (now.layout !== last.layout && !video.paused) fadeOut(fadeRef.current, film, last);
      last = now;
      setLayout(now.layout);
      setShowing(Object.keys(CUES).filter((cue) => time >= CUES[cue][0] && time < CUES[cue][1]).join(" "));
      crawl.style.transform = `translateX(${percent(tickerStart(time), COPIES * TICKER.width)})`;
    };
    const follow = () => {
      read();
      frame = requestAnimationFrame(follow);
    };
    const start = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(follow);
    };
    const stop = () => cancelAnimationFrame(frame);
    video.addEventListener("playing", start);
    video.addEventListener("pause", stop);
    video.addEventListener("seeked", read);
    read();
    if (!video.paused) start();
    return () => {
      stop();
      video.removeEventListener("playing", start);
      video.removeEventListener("pause", stop);
      video.removeEventListener("seeked", read);
    };
  }, [filmRef, fadeRef]);
  return { layout, showing: showing.split(" ") };
}

export default function IntroFilm({ label }) {
  const film = useRef(null);
  const fade = useRef(null);
  const { layout, showing } = useClip(film, fade);
  // The film is a screen and more down the page: its clip, poster and
  // elements wait until the page has settled or the visitor nears it.
  const ready = useDeferredLoad(film);
  const source = (name) => (ready ? `${FILM}/${name}` : undefined);
  const box = ([x, y, width, height]) => ({
    left: percent(x, FRAME[0]),
    top: percent(y, FRAME[1]),
    width: percent(width, FRAME[0]),
    height: percent(height, FRAME[1]),
  });
  // The whole picture, placed so the part the layout shows fills the camera.
  const shown = LAYOUTS[layout].picture;
  const picture = {
    left: percent(-shown[0], shown[2]),
    top: percent(-shown[1], shown[3]),
    width: percent(FRAME[0], shown[2]),
    height: percent(FRAME[1], shown[3]),
  };
  const layer = (part, rect) => (
    <img
      className={`if-layer if-${part}`}
      src={source(`${part}.webp`)}
      alt=""
      decoding="async"
      data-shown={showing.includes(part)}
      data-rect={rect.join(" ")}
      style={box(rect)}
    />
  );
  return (
    <div ref={film} className="if-film" data-layout={layout}>
      <div className="if-camera" style={box(LAYOUTS[layout].camera)}>
        <video src={source("film.mp4")} poster={source("film.jpg")} autoPlay muted loop playsInline aria-label={label} style={picture} />
      </div>
      {layer("list", LAYOUTS.sideBySide.slot)}
      <div
        className="if-layer if-ticker"
        data-shown={showing.includes("ticker")}
        style={{ ...box([0, TICKER.top, FRAME[0], TICKER.height]), background: `linear-gradient(${BAND.map(([stop, colour]) => `${colour} ${percent(stop, 1)}`).join(",")})` }}
      >
        <div className="if-crawl" style={{ width: percent(COPIES * TICKER.width, FRAME[0]) }}>
          {Array.from({ length: COPIES }, (_, k) => (
            <img key={k} src={source("ticker.webp")} alt="" decoding="async" style={{ width: percent(1, COPIES) }} />
          ))}
        </div>
      </div>
      {layer("name", NAME_BAR)}
      <canvas ref={fade} className="if-fade" aria-hidden="true" />
    </div>
  );
}
